/**
 * SealedSenderDemoPage — public-facing technical explainer for the
 * Sealed Sender v1 design. No signup needed; this page exists so
 * security reviewers can audit the claim in 30 seconds.
 *
 * What the page proves:
 *   1. The on-disk `messages` row no longer carries a `sender_id`
 *   2. Messages are authorised by an HMAC `sender_token` instead
 *   3. Wire-level curl + python snippets anyone can paste to verify
 *
 * Route: /privacy/sealed-sender-demo
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Copy,
  Fingerprint,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const _copy = (text, label) => {
  try {
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${label}`);
  } catch {
    toast.error('Clipboard blocked');
  }
};

const CURL_SNIPPET = `# 1. Log in (replace with your own credentials)
TOKEN=$(curl -s -X POST "$BASE/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"..."}' \\
  | python3 -c "import sys,json;print(json.load(sys.stdin)['access_token'])")

# 2. Send a message — note: NO sender_id in the response body
curl -s -X POST "$BASE/api/messages" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"conversation_id":"<conv>","encrypted_content":"...",
       "encrypted_keys":{"<uid>":"..."}}'
# => {"id":"...","sender_token":"<64-hex>", ...}
#    ^^^^^^^^^^^^ no sender_id field, ever.

# 3. Fetch the server row directly — again, no sender_id
curl -s "$BASE/api/messages/<conversation_id>" \\
  -H "Authorization: Bearer $TOKEN" | python3 -m json.tool | head -30`;

const PY_SNIPPET = `import hmac, hashlib, base64, requests

BASE = "https://secure-chat-126.preview.emergentagent.com"

# Fetch the per-conversation opaque secret (participants only)
r = requests.get(f"{BASE}/api/conversations/{conv_id}/sender-secret",
                 headers={"Authorization": f"Bearer {token}"})
secret = base64.b64decode(r.json()["sender_secret"])

# Compute YOUR sender_token exactly the way the server does.
# No user_id ever appears on the message row — peers look up who
# sent each row by finding whichever participant's token matches.
expected = hmac.new(secret, my_user_id.encode(), hashlib.sha256).hexdigest()

# Now send a message and verify the row comes back with that token
# and *nothing else* identifying you.
post = requests.post(f"{BASE}/api/messages",
    headers={"Authorization": f"Bearer {token}"},
    json={"conversation_id": conv_id, "encrypted_content": "...",
          "encrypted_keys": {peer_id: "..."}})
body = post.json()
assert body["sender_token"] == expected
assert "sender_id" not in body          # \u2705 sealed`;

const BeforeAfter = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="rounded-lg border border-rose-500/30 bg-rose-950/20 p-5">
      <div className="flex items-center gap-2 text-rose-300 font-semibold mb-3">
        <XCircle className="w-5 h-5" /> Before (classic messenger)
      </div>
      <pre className="text-xs text-rose-100/80 whitespace-pre-wrap font-mono leading-relaxed">{`{
  "id": "msg_01H…",
  "sender_id": "u_alice",        ← identifies Alice
  "conversation_id": "c_…",
  "encrypted_content": "…",
  "encrypted_keys": { "u_bob": "…" }
}`}</pre>
      <p className="text-xs text-rose-200/70 mt-3">
        A server operator (or subpoena) reading the DB sees <em>who
        sent what</em>, even though the content is E2E-encrypted.
        Metadata is the leak.
      </p>
    </div>

    <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-5">
      <div className="flex items-center gap-2 text-emerald-300 font-semibold mb-3">
        <CheckCircle2 className="w-5 h-5" /> After (Sealed Sender v1)
      </div>
      <pre className="text-xs text-emerald-100/80 whitespace-pre-wrap font-mono leading-relaxed">{`{
  "id": "msg_01H…",
  "sender_token": "9f24b8…",      ← HMAC — no identity
  "conversation_id": "c_…",
  "encrypted_content": "…",
  "encrypted_keys": { "u_bob": "…" }
}`}</pre>
      <p className="text-xs text-emerald-200/70 mt-3">
        The DB row now authenticates messages by an opaque HMAC
        token. Only conversation participants hold the per-conv
        secret needed to map tokens back to user ids. The server
        operator sees: <em>someone in this conversation sent a
        message.</em>
      </p>
    </div>
  </div>
);

export const SealedSenderDemoPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="font-semibold">Government Shield</span>
          </Link>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Fingerprint className="w-4 h-4" />
            Sealed Sender · v1
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12">
        <section className="space-y-4">
          <div className="text-xs uppercase tracking-widest text-primary/80">
            Public audit page
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
            Prove the server doesn't know who sent what.
          </h1>
          <p className="text-slate-300 max-w-2xl text-base leading-relaxed">
            End-to-end encryption hides message bodies. <strong>Sealed
            Sender</strong> hides sender identity from the server row
            itself. No signup required — paste the snippets below and
            verify the wire shape yourself.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold">The schema diff</h2>
          <BeforeAfter />
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Verify with curl</h2>
          <div className="rounded-lg border border-border bg-card/40 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 text-xs text-slate-400">
              <span>terminal</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={() => _copy(CURL_SNIPPET, 'curl snippet')}
                data-testid="copy-curl-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-[11px] text-slate-200 font-mono px-4 py-3 overflow-x-auto whitespace-pre leading-relaxed">
              {CURL_SNIPPET}
            </pre>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Reproduce the HMAC in Python</h2>
          <p className="text-sm text-slate-400 max-w-2xl">
            The per-conversation secret is returned by{' '}
            <code className="text-slate-200">
              GET /api/conversations/&lt;id&gt;/sender-secret
            </code>{' '}
            to participants only. Non-participants get a 404 identical
            in shape to a missing conversation — existence isn't
            leaked by the status code.
          </p>
          <div className="rounded-lg border border-border bg-card/40 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 text-xs text-slate-400">
              <span>python</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={() => _copy(PY_SNIPPET, 'python snippet')}
                data-testid="copy-py-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-[11px] text-slate-200 font-mono px-4 py-3 overflow-x-auto whitespace-pre leading-relaxed">
              {PY_SNIPPET}
            </pre>
          </div>
        </section>

        <section className="space-y-3 pt-4 border-t border-border/60">
          <h2 className="text-xl font-semibold">What this does not solve</h2>
          <ul className="text-sm text-slate-400 space-y-2 max-w-2xl list-disc pl-5">
            <li>
              Traffic-analysis at the network layer — a passive
              observer can still see <em>which account</em> opened a
              TLS connection to the server, though they cannot see
              what that connection did.
            </li>
            <li>
              Side-channels (typing timing, read receipts). We add a
              server-side timing pad ≥50ms before WS broadcast to
              reduce one vector; other channels remain work-in-progress.
            </li>
            <li>
              Compelled cooperation against the sealed-sender secret
              itself — a participant can always reveal who sent what
              within their own conversation. Sealed Sender protects
              against the <em>server</em>, not compromised peers.
            </li>
          </ul>
        </section>

        <section className="pt-8 flex flex-wrap items-center gap-3">
          <Link to="/manifesto">
            <Button variant="secondary" className="text-sm">
              Read the full manifesto <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-sm">
              Back to home
            </Button>
          </Link>
          <span className="text-xs text-slate-500 ml-auto">
            Source: <code>/app/backend/sealed_sender.py</code>,{' '}
            <code>/app/backend/tests/test_sealed_sender.py</code>
          </span>
        </section>
      </main>
    </div>
  );
};

export default SealedSenderDemoPage;
